"use client";

import posthog from "posthog-js";
import { FeedbackLink } from "@/components/FeedbackLink";
import { HoneypotField } from "@/components/HoneypotField";
import { useActionState, useEffect, useRef, useState } from "react";
import {
  subscribeToJobAlerts,
  type SubscribeActionResult,
} from "@/app/job-alerts/actions";

/**
 * Free job-alert email capture (CM-3) — the frictionless freemium funnel.
 * Email + a few optional filter chips (sector / region / grade), no account.
 * On submit the server stores the subscriber durably AND sends the confirm
 * email via Resend (subscribeToJobAlerts -> sendAlertConfirmation) — this
 * path does NOT go through Supabase Auth's magic link, so it is unaffected
 * by T-99 (Supabase Auth SMTP misconfiguration). Only the board's "Create
 * alert" button (JobAlertDialog -> requestJobAlert) routes signed-out users
 * through the magic link and is blocked by T-99; this inline capture and the
 * homepage/board nudge (JobAlertNudge) are not.
 *
 * PostHog funnel (measurable from day one):
 *   job_alert_cta_view    — the capture scrolled into view (once)
 *   job_alert_email_focus — the email field was engaged (once) — added
 *     2026-08-17 to split the 0/84 view->submit gap in two: never engaged
 *     with the form at all (traffic-quality question) vs typed an email and
 *     still bailed (form/trust question, right at the finish line).
 *   job_alert_submit      — the form was submitted
 *   job_alert_success     — the subscription was stored
 */

const SECTORS = [
  "Investment Banking & M&A",
  "Markets & Trading",
  "Asset Management",
  "Private Equity & Venture Capital",
  "Hedge Funds & Alternatives",
  "Retail & Commercial Banking",
  "Fintech & Payments",
  "Insurance & Actuarial",
] as const;

/**
 * Chip values MUST be the board's own vocabulary — parseFilters drops anything
 * it does not recognise, so a wrong label here does not error, it silently
 * widens the alert to every job. These lists were UN grades ("P-3") and
 * invented regions ("Americas") inherited from another product; every one of
 * them was being thrown away. Sourced from REGION_ORDER / GRADE_BAND_ORDER.
 */
const REGIONS = [
  "North America",
  "United Kingdom & Ireland",
  "Europe",
  "Middle East & Africa",
  "Asia-Pacific",
  "Latin America",
  "Global/Remote",
] as const;

const GRADES = [
  "Intern",
  "Graduate",
  "Analyst",
  "Associate",
  "VP",
  "Director",
  "MD",
  "C-Suite",
] as const;

type Group = { param: string; label: string; options: readonly string[] };

const GROUPS: Group[] = [
  { param: "cat", label: "Sector", options: SECTORS },
  { param: "region", label: "Region", options: REGIONS },
  { param: "grade", label: "Level", options: GRADES },
];


export function JobAlertCapture({
  surface = "page",
  className = "",
  headline = "Get new finance jobs by email.",
  description = "Fresh vacancies from across banks, asset managers, hedge funds, PE/VC, fintechs and insurers — straight to your inbox. Free, no account, unsubscribe anytime.",
}: {
  /** Where this instance lives — sent with events for funnel segmentation. */
  surface?: string;
  className?: string;
  /** Override the generic pitch when the surface has context to be specific with (e.g. this job's category). */
  headline?: string;
  description?: string;
}) {
  const [result, formAction, pending] = useActionState<
    SubscribeActionResult | null,
    FormData
  >(subscribeToJobAlerts, null);

  const [selected, setSelected] = useState<Record<string, Set<string>>>({});
  const [filtersOpen, setFiltersOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const viewed = useRef(false);
  const focused = useRef(false);
  const succeeded = useRef(false);

  // CTA view — fire once the capture has been visible for a beat, not the
  // instant it scrolls past. 2026-08-26 session-recording audit (4 sampled
  // job_alert_cta_view sessions): 3/4 were 6-29s total page visits with 0
  // clicks/keypresses — fast scroll-throughs, not people who saw the offer.
  // An instant threshold: 0.3 fire counts those as "views" and inflates the
  // view->submit gap with bounces that were never real chances to convert.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || viewed.current) return;
    const fire = () => {
      if (viewed.current) return;
      viewed.current = true;
      if (posthog.__loaded)
        posthog.capture("job_alert_cta_view", { surface });
    };
    if (typeof IntersectionObserver === "undefined") {
      fire();
      return;
    }
    let dwellTimer: ReturnType<typeof setTimeout> | null = null;
    const io = new IntersectionObserver(
      (entries) => {
        const intersecting = entries.some((e) => e.isIntersecting);
        if (intersecting && dwellTimer === null) {
          dwellTimer = setTimeout(fire, 1500);
        } else if (!intersecting && dwellTimer !== null) {
          clearTimeout(dwellTimer);
          dwellTimer = null;
        }
      },
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => {
      if (dwellTimer !== null) clearTimeout(dwellTimer);
      io.disconnect();
    };
  }, [surface]);

  // Success — fire once when the server confirms the subscription is stored.
  useEffect(() => {
    if (result?.ok && result.state === "subscribed" && !succeeded.current) {
      succeeded.current = true;
      if (posthog.__loaded)
        posthog.capture("job_alert_success", {
          surface,
          already_confirmed: !!result.alreadyConfirmed,
          filter_groups: Object.values(selected).filter((s) => s.size > 0)
            .length,
        });
    }
  }, [result, surface, selected]);

  const toggle = (param: string, value: string) =>
    setSelected((prev) => {
      const next = new Set(prev[param] ?? []);
      next.has(value) ? next.delete(value) : next.add(value);
      return { ...prev, [param]: next };
    });

  const done = result?.ok && result.state === "subscribed";

  if (done) {
    return (
      <div
        ref={rootRef}
        className={`rounded-xl border-2 border-ink bg-white p-6 sm:p-8 ${className}`}
      >
        <p className="telex flex items-center gap-2 text-brand-700">
          <span aria-hidden className="inline-block h-[7px] w-[7px] bg-current" />
          One click to go
        </p>
        <h3 className="display-calm mt-3 text-2xl font-semibold leading-tight text-ink">
          {result?.alreadyConfirmed
            ? "Your alert is already on."
            : "Check your inbox."}
        </h3>
        <p className="mt-3 text-[15px] leading-relaxed text-neutral-600">
          {result?.alreadyConfirmed
            ? "This address is already getting these matches. Nothing else to do — unsubscribe anytime from any email."
            : "We sent you a one-click confirmation link. Click it and we'll start emailing new finance jobs as they go live — no password, no account."}
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-4">
          <a
            href="/jobs"
            className="inline-flex h-11 items-center justify-center rounded-md bg-brand-600 px-5 text-sm font-semibold text-white transition-colors hover:bg-brand-700"
          >
            Browse live vacancies
          </a>
          <a
            href="/auth/login?next=/dashboard"
            rel="nofollow"
            onClick={() =>
              // sendBeacon/send_instantly: this is the exact surface the
              // header_signup-vs-job_alert_success intent comparison needs,
              // and a plain capture() here was losing the race against
              // navigation the same way it was on header/search-tabs/
              // save_job (fixed 2026-09-09).
              posthog.__loaded &&
              posthog.capture(
                "login_cta_click",
                { surface: "job_alert_success" },
                { send_instantly: true, transport: "sendBeacon" },
              )
            }
            className="text-sm font-semibold text-brand-700 underline underline-offset-2 hover:text-brand-800"
          >
            Also want to save jobs &amp; build a page? →
          </a>
        </div>
        <p className="mt-5 border-t border-neutral-100 pt-4 text-sm">
          <FeedbackLink
            kind="job_alert_intent"
            prompt="One question: what role are you hoping this alert finds you?"
            placeholder="e.g. M&A analyst roles in London, entry-level..."
            context={`surface=${surface}`}
            className="text-neutral-500 underline-offset-2 hover:text-brand-700 hover:underline"
          />
        </p>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className={`rounded-xl border-2 border-ink bg-white p-6 sm:p-8 ${className}`}
    >
      <p className="telex flex items-center gap-2 text-brand-700">
        <span aria-hidden className="inline-block h-[7px] w-[7px] bg-current" />
        Free job alerts
      </p>
      <h3 className="display-calm mt-3 text-2xl font-semibold leading-tight text-ink sm:text-3xl">
        {headline}
      </h3>
      <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-neutral-600">
        {description}
      </p>

      <form
        action={formAction}
        onSubmit={() => {
          if (posthog.__loaded)
            posthog.capture("job_alert_submit", {
              surface,
              filter_groups: Object.values(selected).filter((s) => s.size > 0)
                .length,
            });
        }}
        className="mt-6"
      >
        <label className="block">
          <span className="block text-[13px] font-medium text-ink">
            Email address
          </span>
          <input
            type="email"
            name="email"
            required
            autoComplete="email"
            placeholder="you@example.org"
            onFocus={() => {
              if (focused.current) return;
              focused.current = true;
              if (posthog.__loaded)
                posthog.capture("job_alert_email_focus", { surface });
            }}
            onChange={() => {
              // Backstop for browser/password-manager autofill, which can fill
              // this field without ever dispatching a native focus event — the
              // exact gap found and fixed on LoginForm's identical onFocus-only
              // pattern (2026-09-04, commit 34cb261). Applied here preventively:
              // job_alert_email_focus < job_alert_submit hasn't been observed
              // yet, but the component shares the same vulnerable pattern.
              if (focused.current) return;
              focused.current = true;
              if (posthog.__loaded)
                posthog.capture("job_alert_email_focus", { surface });
            }}
            className="mt-1.5 w-full rounded-md border border-neutral-300 bg-white px-3.5 py-2.5 text-[15px] text-ink placeholder:text-neutral-400 focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600 sm:max-w-md"
          />
        </label>

        {/* Collapsed by default (shipped 2026-08-17): the 3-group chip picker
            was always-open and job_alert_cta_view->submit sat at 0/53 since
            the visibility fix landed the day before, with 100% of nudge_shown
            also ending in dismissal. Three open chip rows in front of a
            "just email me" pitch reads as more commitment than "no account,
            one field" promises. Filtering is still one click away, just not
            the first thing shown. */}
        <fieldset className="mt-5">
          {filtersOpen ? (
            <legend className="text-[13px] font-medium text-ink">
              Filter your alerts{" "}
              <span className="font-normal text-neutral-400">
                (optional — leave empty for all jobs)
              </span>
            </legend>
          ) : (
            <button
              type="button"
              onClick={() => setFiltersOpen(true)}
              className="text-[13px] font-medium text-brand-700 underline underline-offset-2 hover:text-brand-800"
            >
              + Filter by sector, region or level (optional)
            </button>
          )}
          {filtersOpen ? (
            <div className="mt-3 space-y-3">
              {GROUPS.map((g) => (
                <div key={g.param}>
                  <p className="telex mb-1.5 text-[8.5px] text-neutral-400">
                    {g.label}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {g.options.map((opt) => {
                      const on = selected[g.param]?.has(opt) ?? false;
                      return (
                        <button
                          type="button"
                          key={opt}
                          aria-pressed={on}
                          onClick={() => toggle(g.param, opt)}
                          className={`inline-flex h-8 items-center rounded-full border px-3 text-[13px] font-medium transition-colors ${
                            on
                              ? "border-brand-600 bg-brand-600 text-white"
                              : "border-neutral-300 bg-white text-neutral-600 hover:border-brand-400 hover:text-ink"
                          }`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </fieldset>

        {/* Selected chips as real form values so the server receives them. */}
        {GROUPS.flatMap((g) =>
          [...(selected[g.param] ?? [])].map((v) => (
            <input
              key={`${g.param}:${v}`}
              type="hidden"
              name={g.param}
              value={v}
            />
          )),
        )}

        <HoneypotField />

        <label className="mt-5 flex max-w-xl items-start gap-2.5 text-[12.5px] leading-relaxed text-neutral-600">
          <input
            type="checkbox"
            name="terms"
            required
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-neutral-400 text-brand-600 focus:ring-brand-600"
          />
          <span>
            I agree to receive job alert emails and accept the{" "}
            <a
              href="/terms"
              target="_blank"
              className="underline underline-offset-2 hover:text-brand-700"
            >
              terms
            </a>{" "}
            and{" "}
            <a
              href="/privacy"
              target="_blank"
              className="underline underline-offset-2 hover:text-brand-700"
            >
              privacy policy
            </a>
            .
          </span>
        </label>

        {result && !result.ok ? (
          <p
            role="alert"
            className="mt-4 rounded-md border border-stamp-100 bg-stamp-50 px-3 py-2 text-sm text-stamp-700"
          >
            {result.message}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={pending}
          className="mt-6 inline-flex h-11 w-full items-center justify-center rounded-md bg-brand-600 px-6 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        >
          {pending ? "Signing you up…" : "Email me new jobs"}
        </button>
        <p className="telex mt-3 text-[8.5px] text-neutral-400">
          We only store your email + filters ·{" "}
          <a href="/privacy" className="underline hover:text-brand-700">
            privacy
          </a>
        </p>
      </form>
    </div>
  );
}
